import { createContext, useCallback, useContext, useEffect, useState, useSyncExternalStore } from "react";
import type { Role } from "@ide/shared";
import { FULL_EDITION } from "@/desktop/edition";
import { ProjectSession, type ConnectionStatus, type Peer, type TreeSnapshot } from "@/collab/session";
import { post } from "@/lib/api";
import type { AppConfig, Me, ProjectDetail, RoomToken } from "@/lib/types";

export interface WorkspaceContextValue {
  session: ProjectSession;
  detail: ProjectDetail;
  me: Me;
  config: AppConfig;
}

const Ctx = createContext<WorkspaceContextValue | null>(null);
export const WorkspaceProvider = Ctx.Provider;

export function useWs(): WorkspaceContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useWs fora do WorkspaceProvider");
  return v;
}

/** O projeto aberto, ou null fora de um projeto (componentes usados dentro e fora dele). */
export function useOptionalWs(): WorkspaceContextValue | null {
  return useContext(Ctx);
}

/** Converte "ws" relativo (/sync) em URL absoluta. */
export function absoluteWs(url: string) {
  if (/^wss?:\/\//.test(url)) return url;
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}${url}`;
}

/** Cria a sessão de colaboração do projeto e a destrói ao sair da página. */
export function useProjectSession(projectId: string, syncUrl: string | undefined) {
  const [session, setSession] = useState<ProjectSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!syncUrl) return;
    const s = new ProjectSession({
      projectId,
      syncUrl: absoluteWs(syncUrl),
      // Guardar o projeto no computador (abrir sem internet) é do app desktop; a web é só nuvem.
      offline: FULL_EDITION,
      fetchTicket: () => post<RoomToken>(`/projects/${projectId}/room-token`),
    });
    let alive = true;
    s.start()
      .then(() => alive && setSession(s))
      .catch((err) => alive && setError(err instanceof Error ? err.message : "Falha ao conectar"));
    return () => {
      alive = false;
      s.destroy();
      setSession(null);
    };
  }, [projectId, syncUrl]);
  return { session, error };
}

/**
 * Lê o estado da sessão como uma store externa. `read` devolve o último valor
 * emitido (estável entre emissões); a assinatura acontece antes da leitura
 * final, então nenhum evento se perde entre a renderização e o efeito.
 */
function useSessionValue<T>(
  session: ProjectSession,
  event: "tree" | "presence" | "status" | "role" | "unsynced" | "trouble",
  read: () => T,
): T {
  const subscribe = useCallback((onChange: () => void) => session.on(event, onChange), [session, event]);
  return useSyncExternalStore(subscribe, read, read);
}

export const useTree = (): TreeSnapshot => {
  const { session } = useWs();
  return useSessionValue(session, "tree", () => session.latest("tree")!);
};

export const usePeers = (): Peer[] => {
  const { session } = useWs();
  return useSessionValue(session, "presence", () => session.latest("presence")!);
};

export const useConnection = (): ConnectionStatus => {
  const { session } = useWs();
  return useSessionValue(session, "status", () => session.status);
};

export const useRole = (): Role | null => {
  const { session } = useWs();
  return useSessionValue(session, "role", () => session.role);
};

/** Por que a sala do projeto não abre (null: tudo certo). */
export const useConnectionTrouble = (): string | null => {
  const { session } = useWs();
  return useSessionValue(session, "trouble", () => session.latest("trouble") ?? null);
};

/** Edições locais ainda não confirmadas pelo servidor. */
export const useUnsynced = (): number => {
  const { session } = useWs();
  return useSessionValue(session, "unsynced", () => session.latest("unsynced") ?? 0);
};

/** Assina um valor externo simples (ex.: provider.authorizedScope). */
export function useExternal<T>(subscribe: (cb: () => void) => () => void, get: () => T): T {
  return useSyncExternalStore(subscribe, get, get);
}
